/* eslint-disable react-hooks/exhaustive-deps */
// -----------------------------------------------------------------------------
// useGuidedChecklist
//
// Loads task-checklists + task-templates for the guided popup experience.
// Returns flat groups (today / later / done) + the "Up Next" item selection.
//
// Visibility gate mirrors AttendedChecklistV2 — feature flag is checked before
// any API call so practices without tasks-v2 never see 403 noise in Sentry.
//
// Data sources:
//   1. /api/v1/practices/{p}/leads/{l}/task-checklists/  → spawned tasks
//   2. /api/v1/practices/{p}/task-templates/?limit=200   → template metadata
//
// Applies-to filter: templates whose `applies_to_statuses` includes
// CONSULTATION_ATTENDED AND that have at least one checklist item on a task.
//
// Grouping (per design spec §5):
//   today  – incomplete items whose task.due_at ≤ end-of-today (incl. overdue)
//   later  – incomplete items with no due_at or due_at > end-of-today
//   done   – items where completed_at is non-null or status === 'completed'
//
// Up Next selection rule (design spec §5):
//   1. Earliest overdue item (task.due_at < start-of-today)
//   2. Else earliest due-today item
//   3. Else first template-ordered incomplete item
// -----------------------------------------------------------------------------
import { useState, useEffect, useCallback, useRef } from 'react';
import * as Sentry from '@sentry/react';
import { useSelector } from 'src/redux/store';
import { callApi } from 'src/utils/apiCall';
import { hasFeatureAccess } from 'src/utils/featurePermission';
import { CHECKLIST_COMPLETION_STATE } from 'src/constants/tasksV2';

const ATTENDED_STATUS = 'CONSULTATION_ATTENDED';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Midnight at the start of today (local). */
function startOfToday() {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
}

/** One millisecond before midnight at the end of today (local). */
function endOfToday() {
    const d = new Date();
    d.setHours(23, 59, 59, 999);
    return d;
}

/** Returns true when the checklist item is completed, skipped, or paused — all are out of the active queue. */
function isItemCompleted(item) {
    if (!item) return false;
    if (item.completed_at != null) return true;
    if (item.status === 'completed') return true;
    // SKIPPED items have completion_state set but completed_at may be null and the
    // task-level status stays 'pending' — they still need to drop out of active.
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.SKIPPED) return true;
    // PAUSED items must also stay out of the active pool after a cache-busted refetch.
    // Without this guard a paused item re-enters the queue as soon as the GET returns.
    if (item.completion_state === CHECKLIST_COMPLETION_STATE.PAUSED) return true;
    return false;
}

// ---------------------------------------------------------------------------
// Treatment filter helper
// ---------------------------------------------------------------------------

/**
 * Filters an array of task-checklist groups to only those that:
 *   1. Belong to a template that applies to CONSULTATION_ATTENDED
 *   2. Belong to a template that matches the lead's active treatment item
 *   3. Contain at least one task whose lead_treatment.id matches matchedLeadTreatmentId
 *      (generic tasks with no lead_treatment always pass condition 3)
 *
 * This prevents tasks spawned for a different lead_treatment row (even when it
 * shares the same treatment_item) from bleeding into the checklist surface.
 *
 * `task.lead_treatment` is always a nested object on this endpoint (consistent
 * with TaskBodyView, TaskBoardView, TaskSlideout etc.) — use `.id` to extract.
 *
 * @param {object[]} groupsData           - Raw groups from task-checklists API
 * @param {Map}      tplStatusMap         - template_id → applies_to_statuses[]
 * @param {Map}      tplTreatmentMap      - template_id → applies_to_treatment_items[]
 * @param {*}        leadTreatmentId      - master TreatmentItem id (for template-level filter)
 * @param {*}        matchedLeadTreatmentId - lead_treatment row id (for task-level filter)
 * @returns {object[]}
 */
export function filterGroupsByTreatment(groupsData, { tplStatusMap, tplTreatmentMap, leadTreatmentId, matchedLeadTreatmentId }) {
    return groupsData.map((g) => {
        // 1. Template must apply to CONSULTATION_ATTENDED
        const statuses = tplStatusMap.get(String(g.template_id)) || [];
        if (!statuses.includes(ATTENDED_STATUS)) return null;

        // 2. Template must match the lead's active treatment item (or be generic)
        const tItems = tplTreatmentMap.get(String(g.template_id)) || [];
        const passesTemplate = tItems.length === 0
            || (leadTreatmentId != null && tItems.some((ti) => String(ti?.id) === String(leadTreatmentId)));
        if (!passesTemplate) return null;

        // 3. Filter tasks to only those belonging to the active lead_treatment row.
        //    Generic tasks (no lead_treatment) always pass.
        //    lead_treatment is always a nested object — confirmed consistent with
        //    TaskBodyView, TaskBoardView, TaskSlideout, TaskDetailPage patterns.
        const tasksForThisTx = (g.tasks || []).filter((task) =>
            task.lead_treatment == null
            || String(task.lead_treatment?.id) === String(matchedLeadTreatmentId)
        );
        if (tasksForThisTx.length === 0) return null;
        return { ...g, tasks: tasksForThisTx };
    }).filter(Boolean);
}

// ---------------------------------------------------------------------------
// Flat-item builder
// ---------------------------------------------------------------------------

/**
 * Converts the nested groups/tasks/items structure into a flat array of items
 * augmented with their parent task's due_at and template metadata.
 *
 * Ordering preserved from API response so that "first template-ordered item"
 * (upNext rule #3) is simply flatItems.find(i => !i.done).
 */
function buildFlatItems(groups, tplStatusMap) {
    const items = [];
    let globalOrder = 0;

    groups.forEach((group) => {
        const statuses = tplStatusMap.get(String(group.template_id)) || [];
        if (!statuses.includes(ATTENDED_STATUS)) return;

        const tasks = group.tasks || [];
        tasks.forEach((task) => {
            const checklistItems = task.checklist_items || [];
            if (checklistItems.length === 0) return;

            // Sort checklist items by sort_order (ascending; nulls last)
            const sortedItems = [...checklistItems].sort(
                (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
            );

            sortedItems.forEach((item) => {
                // #21 — skip structural / parent trigger items that carry no
                // actionable type or display title; they would render as blank cards.
                const resolvedType = item.item_type || item.action_type || '';
                const resolvedTitle = item.title || item.name || '';
                if (!resolvedType && !resolvedTitle) return;

                items.push({
                    // stable identity
                    itemId: item.id ?? item.checklist_item_id,
                    id: item.id ?? item.checklist_item_id,
                    taskId: task.id,
                    taskTitle: task.title,
                    templateId: group.template_id,
                    templateName: group.template_name,
                    // checklist item fields promoted to top level for component access
                    item_type: item.item_type || item.action_type || '',
                    title: item.title || item.name || '',
                    description: item.description || '',
                    completed_at: item.completed_at || null,
                    status: item.status || null,
                    sort_order: item.sort_order ?? null,
                    // due_at lives on the task, not the item
                    due_at: task.due_at || null,
                    // completion state — promoted so DoneCard chip resolves YES/SKIPPED/PAUSED.
                    // is_completed and paused_until are also forwarded so the chip can show
                    // the correct label and the paused-until date without digging into raw.
                    completion_state: item.completion_state || null,
                    is_completed:     item.is_completed ?? null,
                    paused_until:     item.paused_until  || null,
                    done: isItemCompleted(item),
                    // template-ordering for upNext rule #3
                    templateOrder: globalOrder,
                    // pass raw objects through for downstream consumers
                    raw: item,
                    rawTask: task,
                });
                globalOrder += 1;
            });
        });
    });

    return items;
}

// ---------------------------------------------------------------------------
// Group builder
// ---------------------------------------------------------------------------

function buildGroups(flatItems) {
    const todayEnd = endOfToday();

    const today = [];
    const later = [];
    const done = [];

    flatItems.forEach((item) => {
        if (item.done) {
            done.push(item);
            return;
        }
        if (!item.due_at) {
            later.push(item);
            return;
        }
        const d = new Date(item.due_at);
        if (d <= todayEnd) {
            // Covers overdue (d < todayStart) and today (d <= todayEnd)
            today.push(item);
        } else {
            later.push(item);
        }
    });

    // Sort today group: overdue first (ascending due_at), then within-today
    today.sort((a, b) => new Date(a.due_at) - new Date(b.due_at));

    return { today, later, done };
}

// ---------------------------------------------------------------------------
// Up Next selector
// ---------------------------------------------------------------------------

function selectUpNext(flatItems) {
    const todayStart = startOfToday();
    const todayEnd = endOfToday();
    const incomplete = flatItems.filter((i) => !i.done);
    if (incomplete.length === 0) return null;

    // Rule 1: earliest overdue
    const overdue = incomplete
        .filter((i) => i.due_at && new Date(i.due_at) < todayStart)
        .sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
    if (overdue.length > 0) return overdue[0];

    // Rule 2: earliest due today
    const dueToday = incomplete
        .filter((i) => {
            if (!i.due_at) return false;
            const d = new Date(i.due_at);
            return d >= todayStart && d <= todayEnd;
        })
        .sort((a, b) => new Date(a.due_at) - new Date(b.due_at));
    if (dueToday.length > 0) return dueToday[0];

    // Rule 3: first template-ordered incomplete item
    return incomplete.sort((a, b) => a.templateOrder - b.templateOrder)[0];
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * @param {object} params
 * @param {string|number} params.practiceId
 * @param {string|number} params.leadId
 * @param {object}        params.leadDetail  – lead object from Redux
 *
 * @returns {{
 *   loading: boolean,
 *   templatesEmpty: boolean,
 *   groups: { today: object[], later: object[], done: object[] },
 *   upNext: object|null,
 *   refresh: function,
 * }}
 */
export default function useGuidedChecklist({ practiceId, leadId, leadDetail }) {
    const [loading, setLoading] = useState(true);
    // hasDataRef latches to true after the first successful fetch completes.
    // initialLoading = loading && !hasDataRef — so subsequent refreshes don't
    // flip initialLoading back to true, preventing AttendedGuidedChecklistV2
    // from unmounting (and losing local state) mid-refresh.
    const hasDataRef = useRef(false);
    const [templatesEmpty, setTemplatesEmpty] = useState(false);
    const [groups, setGroups] = useState({ today: [], later: [], done: [] });
    const [upNext, setUpNext] = useState(null);
    const [templatePreviewItems, setTemplatePreviewItems] = useState([]);
    // Non-zero when all tasks are complete but BE omits completed checklist items
    // from task.checklist_items (they are filtered server-side). In this case
    // flatItems is empty but group.progress.completed === group.progress.total,
    // so we surface the count here so orchestrators can show the all-done card.
    const [allDoneCount, setAllDoneCount] = useState(0);

    // Re-fetch when a template mutation lands (same pattern as AttendedChecklistV2)
    const templatesUpdatedAt = useSelector((s) => s?.taskTemplate?.templatesUpdatedAt || 0);

    const fetchAll = useCallback(async ({ bustCache = false } = {}) => {
        if (!practiceId || !leadId) {
            setLoading(false);
            setTemplatesEmpty(true);
            return;
        }

        // Feature-gate: never fire API calls for practices without tasks-v2
        if (!hasFeatureAccess('tasks-v2', practiceId)) {
            setLoading(false);
            setTemplatesEmpty(true);
            return;
        }

        setLoading(true);
        try {
            const [checklistsRes, tplRes] = await Promise.all([
                // Pass bustCache through so post-mutation refreshes bypass the 32s GET
                // dedupe window and return real server state instead of a cached snapshot.
                callApi(
                    `/api/v1/practices/${practiceId}/leads/${leadId}/task-checklists/`,
                    'GET', null, false,
                    bustCache ? { bustCache: true } : {}
                ),
                callApi(`/api/v1/practices/${practiceId}/task-templates/?limit=200`),
            ]);

            // Normalise paginated / non-paginated responses
            let groupsData = [];
            if (Array.isArray(checklistsRes?.data)) groupsData = checklistsRes.data;
            else if (Array.isArray(checklistsRes?.data?.results)) groupsData = checklistsRes.data.results;

            let tplData = [];
            if (Array.isArray(tplRes?.data?.results)) tplData = tplRes.data.results;
            else if (Array.isArray(tplRes?.data)) tplData = tplRes.data;

            // Build template → statuses lookup
            const tplStatusMap = new Map();
            tplData.forEach((t) => {
                tplStatusMap.set(
                    String(t.id),
                    Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : []
                );
            });

            // 4th condition: resolve the lead's active treatment id.
            // Priority: ATTENDED → BOOKED → first treatment (handles all status variants).
            // Use treatment_data.treatment_item.id (master TreatmentItem ID, small numbers) —
            // this matches the applies_to_treatment_items[].id on templates.
            // treatment_data.id is the PracticeTreatmentItem ID (large, practice-specific) — wrong space.
            const allTreatments = leadDetail?.lead_treatments || [];
            const matchedTreatment =
                allTreatments.find((t) => t.status === ATTENDED_STATUS) ||
                allTreatments.find((t) => t.status === 'CONSULTATION_BOOKED') ||
                allTreatments[0];
            // Only treatment_item.id is the correct master TreatmentItem ID. The
            // other fields live in different ID spaces and were previously
            // fallbacks — they silently mis-matched, so we no longer use them.
            // If treatment_item.id is missing, leadTreatmentId is null and the
            // treatment-specific filter returns false (so no surface flickers
            // visible due to a wrong-space accidental match).
            const leadTreatmentId = matchedTreatment?.treatment_data?.treatment_item?.id ?? null;
            // lead_treatment row id — used to filter tasks to only this row when a
            // lead has 2+ treatments sharing the same treatment_item.id.
            const matchedLeadTreatmentId = matchedTreatment?.id ?? null;
            if (matchedTreatment && leadTreatmentId == null) {
                Sentry.captureMessage('useGuidedChecklist: leadTreatmentItemId missing on lead_treatment', {
                    level: 'warning',
                    tags: { context: 'guided_checklist_treatment_id' },
                    extra: { practiceId, leadId, treatmentDataKeys: Object.keys(matchedTreatment?.treatment_data || {}) },
                });
            }

            // Returns true when a template passes the treatment-match gate:
            // generic templates (empty applies_to_treatment_items) always pass;
            // treatment-specific ones must include leadTreatmentId.
            // NOTE: applies_to_treatment_items entries are {id, name} objects, not scalars.
            const passesTreatmentFilter = (t) => {
                const tItems = Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : [];
                if (tItems.length === 0) return true; // generic
                if (leadTreatmentId == null) return false;
                return tItems.some((ti) => String(ti?.id) === String(leadTreatmentId));
            };

            // Check whether any applicable templates exist at all (before spawned
            // tasks are considered — used to decide if V1 fallback is needed).
            // Applies both the status filter and the new treatment-match filter.
            const applicableTemplatesExist = tplData.some((t) => {
                const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
                const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                const isActive = t.is_active !== false;
                return statuses.includes(ATTENDED_STATUS) && items.length > 0 && isActive && passesTreatmentFilter(t);
            });


            if (!applicableTemplatesExist) {
                setTemplatesEmpty(true);
                setGroups({ today: [], later: [], done: [] });
                setUpNext(null);
                setTemplatePreviewItems([]);
                setAllDoneCount(0);
                return;
            }

            // Build a per-template treatment lookup for filtering groups
            const tplTreatmentMap = new Map();
            tplData.forEach((t) => {
                tplTreatmentMap.set(
                    String(t.id),
                    Array.isArray(t.applies_to_treatment_items) ? t.applies_to_treatment_items : []
                );
            });

            // Pre-filter groupsData so buildFlatItems and progress-based detection
            // both operate on treatment-matching groups only. Also trims each group's
            // tasks to those belonging to the active lead_treatment row, preventing
            // bleed-through when multiple treatments share the same treatment_item.id.
            const filteredGroupsData = filterGroupsByTreatment(groupsData, {
                tplStatusMap,
                tplTreatmentMap,
                leadTreatmentId,
                matchedLeadTreatmentId,
            });

            // Flat items from spawned task-checklists (treatment-filtered)
            const flatItems = buildFlatItems(filteredGroupsData, tplStatusMap);

            // If no spawned tasks match yet (e.g. status transition hasn't
            // happened), we still consider templates not empty — the gate should
            // still show. Only mark empty when NO applicable template exists.
            setTemplatesEmpty(false);

            // Progress-based all-done detection —————————————————————————————
            // The BE may exclude completed checklist items from task.checklist_items
            // (server-side filtering for performance), causing flatItems to be empty
            // even when all tasks exist and are done. group.progress.completed/total
            // is always returned regardless, so we use it to detect this state.
            // Only count treatment-matching groups (filteredGroupsData already scoped).
            if (flatItems.length === 0) {
                let progressTotal = 0;
                let progressCompleted = 0;
                filteredGroupsData.forEach((group) => {
                    const statuses = tplStatusMap.get(String(group.template_id)) || [];
                    if (!statuses.includes(ATTENDED_STATUS)) return;
                    const p = group.progress || {};
                    progressTotal += (p.total || 0);
                    progressCompleted += (p.completed || 0);
                });
                if (progressTotal > 0 && progressCompleted >= progressTotal) {
                    // All tasks are done — surface the count so the orchestrator
                    // can render the all-done card rather than the preview path.
                    setAllDoneCount(progressTotal);
                    setGroups({ today: [], later: [], done: [] });
                    setUpNext(null);
                    setTemplatePreviewItems([]);
                    return;
                }
            }
            setAllDoneCount(0);

            // When no tasks have been spawned yet, build a preview list from
            // applicable templates so the UI can show a non-interactive preview
            // instead of a "No tasks yet" empty state.
            // Apply treatment filter here too so mismatched templates don't appear.
            if (flatItems.length === 0) {
                const applicable = tplData
                    .filter((t) => {
                        const statuses = Array.isArray(t.applies_to_statuses) ? t.applies_to_statuses : [];
                        const items = Array.isArray(t.checklist_items) ? t.checklist_items : [];
                        return statuses.includes(ATTENDED_STATUS) && items.length > 0 && t.is_active !== false && passesTreatmentFilter(t);
                    })
                    .sort((a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity));
                const previewItems = [];
                applicable.forEach((tpl) => {
                    const sortedCi = [...(tpl.checklist_items || [])].sort(
                        (a, b) => (a.sort_order ?? Infinity) - (b.sort_order ?? Infinity)
                    );
                    sortedCi.forEach((ci, idx) => {
                        previewItems.push({
                            id: `preview-${tpl.id}-${idx}`,
                            title: ci.title || ci.name || '',
                            description: ci.description || '',
                            item_type: ci.item_type || ci.action_type || '',
                            due_at: null,
                            completed_at: null,
                            _preview: true,
                            _templateId: tpl.id,
                            _templateName: tpl.name || '',
                        });
                    });
                });
                setTemplatePreviewItems(previewItems);
            } else {
                setTemplatePreviewItems([]);
            }

            const builtGroups = buildGroups(flatItems);
            const nextItem = selectUpNext(flatItems);

            setGroups(builtGroups);
            setUpNext(nextItem);
        } catch (err) {
            Sentry.captureException(err, { tags: { context: 'guided_checklist_fetch' } });
            // On error: treat as empty so the component falls back to V1
            setTemplatesEmpty(true);
            setGroups({ today: [], later: [], done: [] });
            setUpNext(null);
            setTemplatePreviewItems([]);
            setAllDoneCount(0);
        } finally {
            hasDataRef.current = true;
            setLoading(false);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [practiceId, leadId, JSON.stringify((leadDetail?.lead_treatments || []).map((t) => ({ id: t.id, s: t.status, ti: t.treatment_data?.treatment_item?.id ?? null })))]);

    useEffect(() => {
        fetchAll();
    }, [fetchAll, templatesUpdatedAt]);

    return {
        loading,
        // initialLoading is true only during the very first fetch; subsequent
        // refreshes do NOT flip it back to true (hasDataRef latches). Use this
        // instead of `loading` for the V2 component gate in parent components
        // so that mid-refresh refreshes don't unmount the V2 checklist and
        // discard local state (e.g. recentlyCompleted, featuredOverride).
        initialLoading: loading && !hasDataRef.current,
        templatesEmpty,
        groups,
        upNext,
        templatePreviewItems,
        allDoneCount,
        refresh: fetchAll,
    };
}
